
import { useState, useCallback } from "react";
import { type ActiveSidebar } from "../types/chat-sidebar-types";

interface UseSidebarStateProps {
  initialIsOpen?: boolean;
  onClose?: () => void;
  onToggleFullscreen?: () => void;
  externalIsFullscreen?: boolean;
}

export function useSidebarState({
  initialIsOpen = false,
  onClose,
  onToggleFullscreen,
  externalIsFullscreen
}: UseSidebarStateProps = {}) {
  const [internalIsOpen, setInternalIsOpen] = useState(initialIsOpen);
  const [activeSidebar, setActiveSidebar] = useState<ActiveSidebar>(null);

  // Use external fullscreen state if provided, otherwise manage internally
  // Note: Parent ChatSidebar props has isFullscreen but no setIsFullscreen exposed
  // So we rely on the callback for toggling, but we can track local state if needed.
  // For this refactor, we'll assume the parent controls the boolean if provided.
  
  const isFullscreen = externalIsFullscreen;

  const toggleSidebar = useCallback(() => {
    if (internalIsOpen && onClose) {
      onClose();
    }
    setInternalIsOpen(!internalIsOpen);
  }, [internalIsOpen, onClose]);

  const handleSidebarSelect = useCallback((sidebar: ActiveSidebar) => {
    if (sidebar === activeSidebar) {
      // Clicking the active panel toggles it closed
      setActiveSidebar(null);
      return;
    }
    setActiveSidebar(sidebar);
    if (!internalIsOpen) {
      setInternalIsOpen(true);
    }
  }, [activeSidebar, internalIsOpen]);

  return {
    isOpen: internalIsOpen,
    setIsOpen: setInternalIsOpen,
    activeSidebar,
    setActiveSidebar: handleSidebarSelect,
    toggleSidebar,
    isFullscreen,
    toggleFullscreen: onToggleFullscreen
  };
}
